// ──────────────────────────────────────────────────────────────────────────
// MAS 全站設計 Token（單一來源 / Single Source of Truth）
//
//   對齊 MAS 設計系統 Guideline（Typography / Spacing / Border Radius / Colors）。
//   任何頁面都可引用，改一個地方 → 全站跟著變：
//
//     import { TYPO, COLORS, SPACING, RADIUS } from "theme/tokens";
//     <span style={TYPO.title}>標題</span>
//     <div style={{ ...TYPO.bodyM, color: COLORS.grayDark, padding: SPACING.m, borderRadius: RADIUS.l }} />
//
//   字族：中文 = Noto Sans TC（思源黑體）／ 英文・數字 = Outfit
// ──────────────────────────────────────────────────────────────────────────
const NOTO = 'var(--cjk-font, "Noto Sans TC"), sans-serif';
const OUTFIT = "Outfit, sans-serif";
export const FONT = { NOTO, OUTFIT };

// ── Typography ──────────────────────────────────────────────────────────
//   字級採「行高 = 1.5 × 字級」一致的那組（與 MAS 精靈全站實作一致）
//   Display-L 32/48、Display-M 24/36、Heading-L 24/36(700)、Heading-M 16/24(700)、
//   Title 16/24(500)、Body-M 14/21(400)、Body-S 12/18(400)
export const TYPO = {
  // 中文 / 一般文字（Noto Sans TC）
  displayL: { fontFamily: NOTO, fontSize: 34, fontWeight: 500, lineHeight: "48px" }, // 大型數據展示 / 主標
  displayM: { fontFamily: NOTO, fontSize: 26, fontWeight: 500, lineHeight: "36px" }, // 視窗/頁面/畫面展示標題
  headingL: { fontFamily: NOTO, fontSize: 26, fontWeight: 700, lineHeight: "36px" }, // 頁面主要大標題
  modalTitle: { fontFamily: NOTO, fontSize: 26, fontWeight: 700, lineHeight: "42px" }, // modal 標題（MAS 精靈專用，行高 42）
  headingM: { fontFamily: NOTO, fontSize: 18, fontWeight: 700, lineHeight: "24px" }, // 卡片標題 / 區塊副標題 / Tab
  title: { fontFamily: NOTO, fontSize: 18, fontWeight: 500, lineHeight: "24px" }, // 小標題 / 接組 / 大標版
  bodyM: { fontFamily: NOTO, fontSize: 16, fontWeight: 400, lineHeight: "21px" }, // 頁面內文 / 輸入框 / 說明文字
  bodyS: { fontFamily: NOTO, fontSize: 14, fontWeight: 400, lineHeight: "18px" }, // 輔助說明 / 小標籤 / 小標籤
  bodyXS: { fontFamily: NOTO, fontSize: 13, fontWeight: 400, lineHeight: "18px" }, // ✅ 2026-06-08 密集表格 / 輔助小字（全站最常用 13px，Sean 核准補入）

  // 數字（Outfit）— 對應上面的字級，但用 Outfit
  numDisplayL: { fontFamily: OUTFIT, fontSize: 34, fontWeight: 500, lineHeight: "48px" }, // 預估年化報酬數字
  numKpi: { fontFamily: OUTFIT, fontSize: 26, fontWeight: 500, lineHeight: "36px" }, // 月報酬/勝率/MDD 數字
  numQuota: { fontFamily: OUTFIT, fontSize: 30, fontWeight: 700, lineHeight: 1 },       // 配額卡剩餘數字
  numS: { fontFamily: OUTFIT, fontSize: 14, fontWeight: 400, lineHeight: "18px" },  // symbol / timeframe tag
};

// ── Spacing（Utilities-Spacing：2XS→3XL）────────────────────────────────
export const SPACING = {
  xxs: 2,   // 2XS
  xs: 4,    // XS
  s: 8,     // S
  m: 16,    // M
  l: 24,    // L
  xl: 32,   // XL
  xxl: 48,  // 2XL
  xxxl: 96, // 3XL
};

// ── Border Radius ──────────────────────────────────────────────────────
export const RADIUS = {
  xs: 2,    // XS
  s: 6,     // S
  m: 8,     // M
  l: 12,    // L
  xl: 16,   // XL
  xxl: 20,  // 2XL
  xxxl: 24, // 3XL
  full: 999,
};

// ── Colors ─────────────────────────────────────────────────────────────
//   ✅ = 本 session 已確認/PO 規格出現過的 hex
//   ⚠️ TODO = 設計系統有此色槽但截圖未標 hex，先用 session 近似值，待 PO 校正
export const COLORS = {
  // Brand
  main: "#F36D26", // ✅ Main color（品牌橘）
  yellow: "#F5C965", // ✅ Brand Yellow（漸層起點）
  red: "#D63A26", // ✅ Brand Red（警示文字）
  lighter: "#FEF1EA", // ✅ Lighter（淺橘底）
  gradient: "linear-gradient(90deg, #F5C965 0%, #F36D26 100%)", // ✅ Main + Yellow

  // Dark / Gray
  black: "#000000", // ✅ Brand Black
  grayDark: "#8A8A8A", // ✅ Gray-Dark
  grayMedian: "#C4C4C4", // ✅ Gray-Median
  grayLighter: "#E6E6E6", // ✅ Gray-Lighter
  grayLightest: "#F6F6F6", // ✅ Gray-Lightest
  white: "#FFFFFF",
  pageBg: "#F0F2F5", // ✅ 2026-06-13 主要頁面背景（Sean 指定：AI助理/策略管理/推薦中心/Coin管理/帳務管理）

  // State
  error: "#EB3119", // ✅ Error（另有 Brand Red #D63A26）
  warning: "#FBBA25", // ✅ Warning
  safe: "#71BE29", // ✅ Safe
  purple: "#8E55D7", // ✅ 2026-06-05 PRO/Pro tier 紫（Sean 指定）

  // 風險三色 / 派系 banner（低=保守 / 中=平衡 / 高=積極）— PO 鎖定色
  riskLow: "#DA8D5E", // 保守
  riskMid: "#FBC054", // 平衡
  riskHigh: "#FC8915", // 積極
};

// ── Shadows ────────────────────────────────────────────────────────────
//   格式：offset-x  offset-y  blur  spread  color
export const SHADOWS = {
  s: "0px 2px 6px 0px rgba(0, 0, 0, 0.20)",        // shadow-S（卡片）
  m: "2px 2px 8px 0px rgba(60, 59, 59, 0.15)",     // shadow-M
  l: "6px 6px 12px 0px rgba(60, 59, 59, 0.15)",    // shadow-L
  xl: "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",    // shadow-XL（pop-up / modal）
};

// ── Icon 尺寸 ──────────────────────────────────────────────────────────
export const ICON = {
  m: 24, // M
  l: 42, // L
};

// ── Chart（雷達圖等圖表專用尺寸；不在 typography scale，獨立可調）────────
//   卡片版雷達(我的策略/許願卡/報告)字級在這調；改一處全部跟著變
export const CHART = {
  radarLabelSize: 14,        // 軸標籤字級（報酬力 / 防禦力 …）
  radarLabelColor: "#334155",// 軸標籤顏色
  radarLabelWeight: 800,     // 軸標籤字重
  radarValueSize: 9,         // 頂點數字字級（71 / 98 …）
  radarDotRadius: 11,        // 頂點圓圈半徑
};

export default { TYPO, FONT, SPACING, RADIUS, COLORS, SHADOWS, ICON, CHART };
